/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { HomepageConfig, HomepageProductCard, HomepageProductCardProps } from '../types';
import {
  assuranceTrackerCard,
  cityTrackerCard,
  companyTrackerCard,
  emissionsTrackerCard,
  materialityTrackerCard,
  nationTrackerCard,
  portfolioTrackerCard,
  projectTrackerCard,
  sDGapCard,
  worldTrackerCard,
} from './_common';

// Only logos should be stored in s3, other images should be stored locally
import OneClickFeatureImg from '../../../../images/marketplace/oneclick-feature.png';
import WorldWideTechnologyFeatureImg from '../../../../images/marketplace/worldwidetechnology-feature.png';
import CodeEngineStudioFeatureImg from '../../../../images/marketplace/codeenginestudio-feature.jpg';
import {
  AnchorImg1,
  AnchorImg2,
  AnchorImg3,
  AnchorImg4,
  AnchorImg5,
  CavendishLogo,
  CodeEngineStudioLogo,
  CyberQFeatureImg,
  CyberQLogo,
  FusangLogo,
  KyndrylFeatureImg,
  KyndrylLogo,
  OneClickLogo,
  WorldWideTechnologyLogo,
} from './imageConfig';

export const homepageConfig: HomepageConfig = {
  productCards: (props: HomepageProductCardProps): HomepageProductCard[] => [
    materialityTrackerCard(props),
    companyTrackerCard(props),
    emissionsTrackerCard(props),
    assuranceTrackerCard(props),
    portfolioTrackerCard(props),
    worldTrackerCard(props),
    sDGapCard(props),
  ],
  anchorCards: [
    {
      image: AnchorImg1,
      title: 'Reporting Tools',
      anchor: 'reporting-tools',
    },
    {
      image: AnchorImg2,
      title: 'Education and Training',
      anchor: 'education-and-training',
    },
    {
      image: AnchorImg3,
      title: 'Consultancy',
      anchor: 'consultancy',
    },
    {
      image: AnchorImg4,
      title: 'Sustainability Solutions',
      anchor: 'sustainable-solutions',
    },
    {
      image: AnchorImg5,
      title: 'Sustainable Finance',
      anchor: 'sustainable-finance',
    },
  ],
  featureCards: [
    {
      from: {
        logo: KyndrylLogo,
      },
      cards: [
        {
          image: KyndrylFeatureImg,
          title: 'Tech & Sustainability Consulting',
          description:
            'Including Microsoft Sustainability Manager; Device Enterprise Management on ESG factors; Kyndryl Mindful employee well-being consulting; Employee Dashboard for elevated employee engagement programs.',
        },
      ],
    },
    {
      from: {
        logo: CyberQLogo,
      },
      cards: [
        {
          image: CyberQFeatureImg,
          title: 'Cyber Security Governance',
          description: `A cyber framework that makes it easy to introduce cyber expertise and services
        into your organisation as you need them. Every business is on a different journey so you need
        a cyber partner that protects you now and can scale.`,
        },
      ],
    },
    {
      from: {
        logo: OneClickLogo,
      },
      cards: [
        {
          image: OneClickFeatureImg,
          title: 'IT efficiency and savings',
          description:
            'Enables organisations to modernise their IT systems and applications, improving growth, operational efficiency, and resilience to disruption. The platform offers a modular and easy-to-use solutions.',
          imageHeight: 25,
        },
      ],
    },
    {
      from: {
        logo: WorldWideTechnologyLogo,
      },
      cards: [
        {
          image: WorldWideTechnologyFeatureImg,
          title: 'IT consultancy and solutions',
          description:
            'Leading technology solutions provider helping organisations accelerate their digital transformation journeys. Expertise across various domains, including AI, cloud, cybersecurity, and infrastructure modernisation.',
          imageHeight: 40,
        },
      ],
    },
    {
      from: {
        logo: CodeEngineStudioLogo,
      },
      cards: [
        {
          image: CodeEngineStudioFeatureImg,
          title: 'Software coding services',
          description:
            'Specialises in building software for your company, and working on solutions to solve real business problems. Expertise includes Java, Node.js, PHP, React.js, Vue.js, headless CMS, and more.',
          imageHeight: 25,
        },
      ],
    },
  ],
  featureBarCards: {
    categoryOne: {
      icon: 'fa-money-check-dollar-pen',
      title: 'Liquidity Events',
    },
    categoryTwo: {
      icon: 'fa-coins',
      title: 'Microfinance',
    },
    categoryThree: {
      icon: 'fa-money-bill-transfer',
      title: 'Sustainability Rebates',
    },
    cardLeft: {
      image: FusangLogo,
      title: `Connects private markets and digital assets. Fusang's
    Exchange provides seamless access to liquidity at scale`,
      list: [
        { text: 'Sukuks' },
        { text: 'Equities' },
        { text: 'Access liquidity globally' },
        { text: 'Security Tokenisation Social, Economic and Environmental Impact Bonds' },
      ],
    },
    cardRight: {
      image: CavendishLogo,
      title:
        'Expert services in equity capital markets, M&A, debt advisory, and private growth capital to public and private companies',
      list: [
        { text: 'Equity Capital Markets' },
        { text: 'Debt Advisory' },
        { text: 'Private Growth Capital' },
        { text: 'Private & Public M&A' },
      ],
    },
  },
  comingSoonProductCards: [projectTrackerCard, cityTrackerCard, nationTrackerCard],
};
